import { Link, useParams } from "react-router-dom";
import { ArrowRight, Info } from "lucide-react";
import { Seo } from "@/lib/seo";
import { CtaBlock } from "@/components/cta";
import { Reveal } from "@/lib/motion";
import { guiaBySlug } from "@/content/guides";
import { momentoBySlug } from "@/content/moments";
import { servicioBySlug } from "@/content/services";
import { DISCLAIMER_GUIAS } from "@/content/site";

export default function GuiaDetalle() {
  const { slug } = useParams();
  const guia = guiaBySlug(slug);

  if (!guia) {
    return (
      <div className="mx-auto max-w-3xl px-4 py-24 text-center sm:px-8">
        <p className="eyebrow mb-4">Guía no encontrada</p>
        <h1 className="font-serif text-4xl text-ink-deep">Esta guía no existe</h1>
        <Link to="/guias" data-testid="guia-volver-index" className="mt-6 inline-flex text-azul underline underline-offset-4">
          Ver todas las guías
        </Link>
      </div>
    );
  }

  const momento = guia.momentoRelacionado ? momentoBySlug(guia.momentoRelacionado) : null;
  const servicio = guia.servicioRelacionado ? servicioBySlug(guia.servicioRelacionado) : null;

  return (
    <>
      <Seo title={guia.seoTitle} description={guia.seoDesc} path={`/guias/${guia.slug}`} type="article" />

      <article className="bg-parchment">
        <header className="border-b border-hairline">
          <div className="mx-auto max-w-3xl px-4 pb-12 pt-16 sm:px-8 sm:pb-16 sm:pt-24">
            <Reveal y={16}>
              <p className="eyebrow mb-5">
                Guía · {guia.lecturaMin} min de lectura
              </p>
              <h1 className="font-serif text-4xl leading-[1.1] tracking-tight text-ink-deep sm:text-5xl">
                {guia.titulo}
              </h1>
              <p className="mt-6 text-lg leading-relaxed text-ink-muted">{guia.resumen}</p>
            </Reveal>
          </div>
        </header>

        <div className="mx-auto max-w-3xl px-4 py-12 sm:px-8 sm:py-16">
          {guia.secciones.map((seccion, i) => (
            <Reveal key={i} y={16}>
              <section className="mb-12" data-testid={`guia-seccion-${i}`}>
                <h2 className="font-serif text-2xl text-ink-deep sm:text-3xl">
                  <span className="mr-3 font-mono text-sm text-azul">{String(i + 1).padStart(2, "0")}</span>
                  {seccion.titulo}
                </h2>
                {seccion.parrafos?.map((p, j) => (
                  <p key={j} className="mt-5 leading-relaxed text-ink-text">
                    {p}
                  </p>
                ))}
                {seccion.lista && (
                  <ul className="mt-6 space-y-3">
                    {seccion.lista.map((item, j) => (
                      <li key={j} className="flex items-start gap-3 text-ink-text">
                        <span className="mt-2.5 h-1.5 w-1.5 shrink-0 rounded-full bg-azul" />
                        <span className="leading-relaxed">{item}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </section>
            </Reveal>
          ))}

          <Reveal>
            <div className="flex items-start gap-3 border-l-2 border-azul bg-azul-light/40 p-6" data-testid="guia-disclaimer">
              <Info className="mt-0.5 h-4 w-4 shrink-0 text-azul" />
              <p className="text-sm leading-relaxed text-ink-text">{DISCLAIMER_GUIAS}</p>
            </div>
          </Reveal>

          {(momento || servicio) && (
            <Reveal>
              <div className="mt-12 grid gap-4 sm:grid-cols-2">
                {momento && (
                  <Link
                    to={`/momentos/${momento.slug}`}
                    data-testid="guia-momento-link"
                    className="group border border-hairline p-6 transition-colors hover:border-azul/50"
                  >
                    <p className="eyebrow mb-2">Momento relacionado</p>
                    <span className="inline-flex items-center gap-2 font-serif text-xl text-ink-deep">
                      {momento.nombre}
                      <ArrowRight className="h-4 w-4 text-azul transition-transform group-hover:translate-x-1" />
                    </span>
                  </Link>
                )}
                {servicio && (
                  <Link
                    to={`/servicios/${servicio.slug}`}
                    data-testid="guia-servicio-link"
                    className="group border border-hairline p-6 transition-colors hover:border-azul/50"
                  >
                    <p className="eyebrow mb-2">Servicio relacionado</p>
                    <span className="inline-flex items-center gap-2 font-serif text-xl text-ink-deep">
                      {servicio.nombre}
                      <ArrowRight className="h-4 w-4 text-azul transition-transform group-hover:translate-x-1" />
                    </span>
                  </Link>
                )}
              </div>
            </Reveal>
          )}
        </div>
      </article>

      <CtaBlock
        titulo="¿Ya leíste y toca actuar?"
        sub="Cuéntanos tu situación con lo que tienes a mano. Te decimos qué falta y cómo seguir."
      />
    </>
  );
}
